import React, { useState, useMemo, useRef, useEffect } from "react";
import { Scissors, Sandwich, Store, Globe, Loader2 } from "lucide-react";

/**
 * Replace the URLs below with real client sites when available.
 * Note: Some sites block iframe embedding via X-Frame-Options or
 * Content-Security-Policy. Prefer client sites that allow embedding,
 * or host static preview snapshots.
 */
const CATEGORIES = [
  {
    id: "barbers",
    label: "Barbers",
    Icon: Scissors,
    items: [
      {
        id: "barbers-1",
        name: "Yair Harush Barber",
        city: "Portland, OR",
        url: "https://example.com/",
      },
      {
        id: "barbers-2",
        name: "Oak & Ash Shop",
        city: "Austin, TX",
        url: "https://example.com/",
      },
      {
        id: "barbers-3",
        name: "Ironbird Cuts",
        city: "Brooklyn, NY",
        url: "https://example.com/",
      },
    ],
  },
  {
    id: "delis",
    label: "Delis",
    Icon: Sandwich,
    items: [
      {
        id: "delis-1",
        name: "Holy Bagel",
        city: "Chicago, IL",
        url: "https://example.com/",
      },
      {
        id: "delis-2",
        name: "Bluebird Sandwiches",
        city: "Seattle, WA",
        url: "https://example.com/",
      },
    ],
  },
  {
    id: "stores",
    label: "Stores",
    Icon: Store,
    items: [
      {
        id: "stores-1",
        name: "Heron & Co. Goods",
        city: "Denver, CO",
        url: "https://example.com/",
      },
      {
        id: "stores-2",
        name: "Sable Supply",
        city: "San Diego, CA",
        url: "https://example.com/",
      },
      {
        id: "stores-3",
        name: "Field Mercantile",
        city: "Nashville, TN",
        url: "https://example.com/",
      },
    ],
  },
];

export default function Work() {
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].id);
  const [activeItemId, setActiveItemId] = useState(CATEGORIES[0].items[0].id);
  const [loading, setLoading] = useState(true);

  const category = useMemo(
    () => CATEGORIES.find((c) => c.id === activeCat) || CATEGORIES[0],
    [activeCat]
  );

  const item = useMemo(
    () =>
      category.items.find((i) => i.id === activeItemId) || category.items[0],
    [category, activeItemId]
  );

  const pickCategory = (id) => {
    const next = CATEGORIES.find((c) => c.id === id);
    if (!next) return;
    setActiveCat(id);
    setActiveItemId(next.items[0].id);
    setLoading(true);
  };

  const pickItem = (id) => {
    if (id === activeItemId) return;
    setActiveItemId(id);
    setLoading(true);
  };

  const host = item?.displayHost || safeHost(item?.url);

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-[rgba(10,10,10,0.08)]"
    >
      <div className="section-container">
        <div className="mx-auto max-w-[1040px]">
          <div className="reveal tick-label" data-testid="work-eyebrow">
            Recent work
          </div>
          <h2
            className="reveal font-sans-display mt-4 sm:mt-5 text-[30px] sm:text-[44px] lg:text-[56px] font-bold leading-[1.08] sm:leading-[1.05] tracking-[-0.03em] text-[#0a0a0a]"
            data-testid="work-heading"
          >
            Real sites for{" "}
            <span className="font-display italic font-normal">real</span>{" "}
            businesses.
          </h2>
          <p
            className="reveal reveal-delay-1 mt-4 sm:mt-5 text-[15.5px] sm:text-[18px] leading-[1.6] text-[#0a0a0a]/70 max-w-[620px]"
            data-testid="work-paragraph"
          >
            A small selection of recent builds. Live sites, embedded right
            here — scroll, click, poke around.
          </p>

          {/* Category tabs */}
          <div
            className="reveal reveal-delay-2 mt-8 sm:mt-10 flex flex-wrap gap-2"
            role="tablist"
            aria-label="Work categories"
            data-testid="work-category-tabs"
          >
            {CATEGORIES.map((c) => {
              const isActive = c.id === activeCat;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => pickCategory(c.id)}
                  className={`group inline-flex items-center gap-2 h-10 px-4 rounded-full text-[13.5px] font-medium border transition-all duration-200 ${
                    isActive
                      ? "bg-[#0a0a0a] text-[#fbf9f4] border-[#0a0a0a] shadow-[0_6px_18px_-10px_rgba(10,10,10,0.45)]"
                      : "bg-white text-[#0a0a0a] border-[rgba(10,10,10,0.12)] hover:border-[#0a0a0a]"
                  }`}
                  data-testid={`work-category-tab-${c.id}`}
                >
                  <c.Icon
                    className={`size-3.5 ${
                      isActive ? "text-[#facc15]" : "text-[#0a0a0a]/60"
                    }`}
                    strokeWidth={2}
                  />
                  {c.label}
                  <span
                    className={`font-mono-mark text-[11px] ${
                      isActive ? "text-[#fbf9f4]/60" : "text-[#0a0a0a]/40"
                    }`}
                  >
                    {String(c.items.length).padStart(2, "0")}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Main preview + thumbnail list */}
          <div className="reveal reveal-delay-3 mt-6 sm:mt-8 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 lg:gap-6 items-start">
            {/* Browser-chromed preview */}
            <div
              className="relative rounded-2xl border border-[rgba(10,10,10,0.12)] bg-white overflow-hidden shadow-[0_30px_60px_-30px_rgba(10,10,10,0.3)]"
              data-testid="work-preview-card"
            >
              {/* Chrome bar */}
              <div className="flex items-center gap-3 h-10 px-4 border-b border-[rgba(10,10,10,0.08)] bg-[hsl(var(--background))]">
                <div className="flex items-center gap-1.5" aria-hidden>
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
                  <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#28c840]" />
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="inline-flex items-center gap-1.5 h-6 px-3 rounded-full bg-white border border-[rgba(10,10,10,0.08)] text-[11.5px] text-[#0a0a0a]/60 font-mono-mark max-w-[280px] truncate">
                    <Globe className="size-3 shrink-0 text-[#0a0a0a]/45" />
                    <span className="truncate">{host}</span>
                  </div>
                </div>
                <div className="w-[48px]" aria-hidden />
              </div>

              {/* Iframe area — aspect-ratio box, scaled to fit desktop layout */}
              <ScaledIframe
                key={item.id}
                src={item.url}
                title={item.name}
                loading={loading}
                onLoad={() => setLoading(false)}
              />

              {/* Caption */}
              <div className="flex items-center justify-between gap-4 px-4 sm:px-5 py-3 border-t border-[rgba(10,10,10,0.08)] bg-white">
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold tracking-tight text-[#0a0a0a] truncate">
                    {item.name}
                  </div>
                  <div className="text-[12px] text-[#0a0a0a]/55 truncate">
                    {item.city} · {category.label}
                  </div>
                </div>
                <span className="font-mono-mark text-[10.5px] tracking-[0.12em] uppercase text-[#0a0a0a]/50 shrink-0">
                  Live preview
                </span>
              </div>
            </div>

            {/* Thumbnail list */}
            <ul
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2.5"
              role="tablist"
              aria-label={`${category.label} projects`}
              data-testid="work-item-list"
            >
              {category.items.map((it) => {
                const isActive = it.id === activeItemId;
                return (
                  <li key={it.id}>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => pickItem(it.id)}
                      className={`w-full group relative flex items-center gap-3 rounded-xl border p-3 text-left transition-all duration-200 ${
                        isActive
                          ? "bg-white border-[#0a0a0a] shadow-[0_10px_28px_-18px_rgba(10,10,10,0.3)]"
                          : "bg-white/70 border-[rgba(10,10,10,0.1)] hover:border-[#0a0a0a]/60 hover:-translate-y-0.5"
                      }`}
                      data-testid={`work-item-${it.id}`}
                    >
                      <span
                        className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                          isActive
                            ? "bg-[#0a0a0a] text-[#fbf9f4]"
                            : "bg-[hsl(var(--background))] text-[#0a0a0a]/70"
                        }`}
                      >
                        <category.Icon className="size-4" strokeWidth={2} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13.5px] font-semibold tracking-tight text-[#0a0a0a] truncate">
                          {it.name}
                        </span>
                        <span className="block text-[11.5px] text-[#0a0a0a]/55 truncate">
                          {it.city}
                        </span>
                      </span>
                      <span
                        className={`font-mono-mark text-[10.5px] ${
                          isActive
                            ? "text-[#0a0a0a]"
                            : "text-[#0a0a0a]/35"
                        }`}
                      >
                        {isActive ? "▶" : "·"}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Helper note */}
          <p
            className="reveal mt-4 text-[11.5px] text-[#0a0a0a]/45 max-w-[640px]"
            data-testid="work-embed-note"
          >
            Live embeds — scroll and click within the frame. Previews are
            scaled to show the full desktop layout.
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * Renders an iframe sized at a virtual desktop width (1440×900) and scales it
 * down to fit the container width while preserving a 16:10 aspect ratio.
 */
function ScaledIframe({ src, title, loading, onLoad }) {
  const BASE_W = 1440;
  const BASE_H = 900;
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      setScale(w > 0 ? w / BASE_W : 1);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden bg-[hsl(var(--background))]"
      style={{ aspectRatio: "16 / 10" }}
    >
      {loading ? (
        <div
          className="absolute inset-0 flex items-center justify-center text-[#0a0a0a]/40 z-10"
          data-testid="work-preview-loading"
        >
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : null}
      <div
        className="absolute top-0 left-0"
        style={{
          width: `${BASE_W}px`,
          height: `${BASE_H}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      >
        <iframe
          title={title}
          src={src}
          loading="lazy"
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-forms"
          onLoad={onLoad}
          className="block w-full h-full border-0 bg-white"
          data-testid="work-preview-iframe"
        />
      </div>
    </div>
  );
}

function safeHost(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch (_) {
    return url;
  }
}
